import { KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES } from "../data/enums"
import { extractMediaData } from "./knowledgeMediaHelpers"
import { deepRemoveTypenames } from "./data"

// Recursively remove 'open' attributes from content since GraphQL schema doesn't accept them
const removeOpenAttributes = (content) => {
  if (!content) return content

  if (Array.isArray(content)) {
    return content.map(removeOpenAttributes)
  }

  if (typeof content === "object" && content !== null) {
    const cleaned = { ...content }

    // Remove 'open' from attrs if it exists
    if (cleaned.attrs && typeof cleaned.attrs === "object" && "open" in cleaned.attrs) {
      const { open, ...restAttrs } = cleaned.attrs
      cleaned.attrs = restAttrs
    }

    // Recursively clean nested content
    if (cleaned.content) {
      cleaned.content = removeOpenAttributes(cleaned.content)
    }

    return cleaned
  }

  return content
}

// Normalize mention node attributes to ensure proper format
const normalizeMentionAttrs = (attrs) => {
  if (!attrs) return { id: "unknown", label: "unknown" }

  let normalizedAttrs
  // If attrs is an array, take the first item
  if (Array.isArray(attrs)) {
    normalizedAttrs = attrs[0] || { id: "unknown", label: "unknown" }
  } else {
    // If attrs is already an object, use it
    normalizedAttrs = attrs
  }

  // Remove mentionSuggestionChar if it exists
  const { mentionSuggestionChar, ...cleanAttrs } = normalizedAttrs

  return cleanAttrs
}

// Helpers for transforming specific content types for saving
const transformHeadingItem = (item) => {
  const newContent = Array.isArray(item.content)
    ? item.content.map((c) => {
        return { ...c, attrs: { isHeading: true, headingLevel: item.attrs?.level || 1 } }
      })
    : [{ type: "paragraph", text: item.content, marks: [] }]

  return {
    text: {
      text: newContent
    }
  }
}

const transformImageItem = (imageItem) => {
  const result = {
    image: {
      id: imageItem.id,
      uploads:
        imageItem.attrs?.uploads?.map((upload) =>
          deepRemoveTypenames({
            id: upload.id,
            customSize: upload.customSize
          })
        ) || []
    }
  }

  return result
}

const transformVideoItem = (videoItem) => {
  return {
    video: {
      id: videoItem.id,
      url: videoItem.attrs?.url || ""
    }
  }
}

const transformFilesItem = (filesItem) => {
  return {
    files: {
      id: filesItem.id,
      heading: filesItem.attrs?.heading,
      uploads: filesItem.attrs?.uploads?.map((upload) => {
        return upload.id
      })
    }
  }
}

const transformTextItem = (textItem) => {
  let content = Array.isArray(textItem.content)
    ? textItem.content
    : [{ type: "paragraph", text: textItem.content || "", marks: [] }]

  // Clean up mention attributes
  content = content.map((item) => {
    if (item.type === "mention" && item.attrs) {
      return { ...item, attrs: normalizeMentionAttrs(item.attrs) }
    }
    return item
  })

  return {
    text: {
      text: content
    }
  }
}

// Transform mention items for saving
const transformMentionItem = (item) => {
  return {
    text: {
      text: [{ ...item, attrs: normalizeMentionAttrs(item.attrs) }]
    }
  }
}

const transformNoteItem = (noteItem) => {
  const newContent = noteItem.content.map((item) => {
    return item
  })
  return {
    note: {
      id: noteItem.id,
      text: JSON.stringify(newContent),
      type: noteItem.attrs.type
    }
  }
}

const transformListItem = (item) => {
  const uploads = []

  // Recursive function to process content at any depth
  const processContent = (content) => {
    if (!Array.isArray(content)) return content

    return content.map((contentItem) => {
      // Handle media nodes (images, files, videos) using our common method
      const mediaResult = extractMediaData(contentItem, uploads)
      if (mediaResult) {
        return mediaResult
      }

      // Handle headings within lists
      if (contentItem.type === KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.HEADING) {
        const newContent = Array.isArray(contentItem.content)
          ? contentItem.content.map((c) => {
              return { ...c, attrs: { isHeading: true, headingLevel: contentItem.attrs?.level || 1 } }
            })
          : [{ type: "paragraph", text: contentItem.content, marks: [] }]

        return {
          ...contentItem,
          content: newContent
        }
      }

      // Handle nested lists (bulletList, orderedList)
      if (
        (contentItem.type === KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.BULLET_LIST ||
          contentItem.type === KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.ORDERED_LIST) &&
        contentItem.content
      ) {
        return {
          ...contentItem,
          content: processContent(contentItem.content)
        }
      }

      // Handle list items
      if (contentItem.type === "listItem" && contentItem.content) {
        return {
          ...contentItem,
          content: processContent(contentItem.content)
        }
      }

      // Handle any other content with nested content
      if (contentItem.content && Array.isArray(contentItem.content)) {
        return {
          ...contentItem,
          content: processContent(contentItem.content)
        }
      }

      return contentItem
    })
  }

  const transformedContent = processContent(item.content)

  const newItem = {
    ...item,
    content: transformedContent
  }

  return {
    text: {
      text: {
        ...newItem
      },
      uploads
    }
  }
}

const transformTableItem = (tableItem) => {
  return {
    text: {
      text: {
        ...tableItem
      }
    }
  }
}

const transformHorizontalRuleItem = () => {
  // Note: Horizontal rules are simple separator elements with no attributes or content in TipTap.
  // They render as plain <hr> tags, so we only need to preserve the type.
  // Unlike tables or images, there are no attributes to spread.
  return {
    text: {
      text: {
        type: KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.HORIZONTAL_RULE
      }
    }
  }
}

const transformDetailsItem = (item) => {
  const uploads = []

  // Recursive function to process content at any depth
  const processContent = (content) => {
    if (!Array.isArray(content)) return content

    return content.map((contentItem) => {
      // Handle media nodes (images, files, videos) using our common method
      const mediaResult = extractMediaData(contentItem, uploads)
      if (mediaResult) {
        return mediaResult
      }

      // Handle headings within details
      if (contentItem.type === KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.HEADING) {
        const newContent = Array.isArray(contentItem.content)
          ? contentItem.content.map((c) => {
              return { ...c, attrs: { isHeading: true, headingLevel: contentItem.attrs?.level || 1 } }
            })
          : [{ type: "paragraph", text: contentItem.content, marks: [] }]

        return {
          ...contentItem,
          content: newContent
        }
      }

      // Handle nested lists within details
      if (
        (contentItem.type === KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.BULLET_LIST ||
          contentItem.type === KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.ORDERED_LIST) &&
        contentItem.content
      ) {
        return {
          ...contentItem,
          content: processContent(contentItem.content)
        }
      }

      // Handle list items within details
      if (contentItem.type === KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.LIST_ITEM && contentItem.content) {
        return {
          ...contentItem,
          content: processContent(contentItem.content)
        }
      }

      // Handle nested details (details within details)
      if (contentItem.type === KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.DETAILS && contentItem.content) {
        return {
          ...contentItem,
          content: processContent(contentItem.content)
        }
      }

      // Handle details summary and content
      if (
        (contentItem.type === KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.DETAILS_SUMMARY ||
          contentItem.type === KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.DETAILS_CONTENT) &&
        contentItem.content
      ) {
        return {
          ...contentItem,
          content: processContent(contentItem.content)
        }
      }

      // Handle any other content with nested content
      if (contentItem.content && Array.isArray(contentItem.content)) {
        return {
          ...contentItem,
          content: processContent(contentItem.content)
        }
      }

      return contentItem
    })
  }

  const transformedContent = processContent(item.content)

  const newItem = {
    ...item,
    content: transformedContent
  }

  return {
    text: {
      text: {
        ...newItem
      },
      uploads
    }
  }
}

// Transform a single content item based on its type
const transformContentForSaving = (contentItem) => {
  if (!contentItem || !contentItem.type) {
    return null
  }

  // First, remove all 'open' attributes from the content
  const cleanedContentItem = removeOpenAttributes(contentItem)

  switch (cleanedContentItem.type) {
    case KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.HEADING:
      return transformHeadingItem(cleanedContentItem)
    case KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.TEXT:
      return transformTextItem(cleanedContentItem)
    case KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.DETAILS:
      return transformDetailsItem(cleanedContentItem)
    case KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.BULLET_LIST:
    case KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.ORDERED_LIST:
      return transformListItem(cleanedContentItem)
    case KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.NOTE:
      return transformNoteItem(cleanedContentItem)
    case KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.IMAGE:
      return transformImageItem(cleanedContentItem)
    case KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.FILES:
      return transformFilesItem(cleanedContentItem)
    case KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.VIDEO:
      return transformVideoItem(cleanedContentItem)
    case KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.MENTION:
      return transformMentionItem(cleanedContentItem)
    case KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.TABLE:
      return transformTableItem(cleanedContentItem)
    case KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.HORIZONTAL_RULE:
      return transformHorizontalRuleItem(cleanedContentItem)
    default:
      return null
  }
}

export {
  transformHeadingItem,
  transformImageItem,
  transformVideoItem,
  transformFilesItem,
  transformTextItem,
  transformNoteItem,
  transformListItem,
  transformMentionItem,
  transformHorizontalRuleItem,
  transformContentForSaving,
  normalizeMentionAttrs,
  removeOpenAttributes
}
